import type { TFunction } from 'i18next';
import type {
  OperatorScorecardStatsDto,
  OperatorPerformanceBand,
  FailingSiteStatus,
} from '~/app/services/operator-scorecard/operator-scorecard.types';
import type { ImpactTone } from '~/app/components/dashboard/StatusImpactPill/StatusImpactPill';
import type {
  FailingSiteRowViewModel,
  OperatorCardViewModel,
  ScorecardSummaryCardViewModel,
} from './ScorecardPage.types';

export const SCORECARD_PAGE_SIZE = 5;

const PERFORMANCE_STROKE: Record<OperatorPerformanceBand, string> = {
  excellent: '#16a34a',
  good: '#2563eb',
  needsImprovement: '#ea580c',
};

const STATUS_TONE: Record<FailingSiteStatus, ImpactTone> = {
  urgent: 'critical',
  watching: 'warning',
  good: 'good',
};

function clampPercent(value: number, max: number): number {
  if (max <= 0) return 0;
  return Math.min(100, Math.round((value / max) * 100));
}

function hoursTowardTargetPercent(value: number, target: number): number {
  if (value <= 0) return 100;
  // Lower is better — invert vs target, capped.
  return Math.min(100, Math.round((target / value) * 100));
}

export function formatHours(hours: number): string {
  return `${hours} hours`;
}

export function formatPenaltiesEuros(amount: number, locale: string): string {
  if (amount === 0) {
    return new Intl.NumberFormat(locale, {
      style: 'currency',
      currency: 'EUR',
      maximumFractionDigits: 0,
    }).format(0);
  }
  if (amount >= 1000) {
    const compact = amount / 1000;
    const formatted = Number.isInteger(compact)
      ? String(compact)
      : compact.toFixed(1);
    return `€${formatted}k`;
  }
  return new Intl.NumberFormat(locale, {
    style: 'currency',
    currency: 'EUR',
    maximumFractionDigits: 0,
  }).format(amount);
}

export function mapSummaryCards(
  stats: OperatorScorecardStatsDto,
  t: TFunction<'dashboard'>,
): ScorecardSummaryCardViewModel[] {
  const { summary } = stats;
  return [
    {
      id: 'overall-compliance',
      title: t('scorecard.summary.overallCompliance'),
      metric: `${summary.overallCompliance.score} / ${summary.overallCompliance.max}`,
      description: t('scorecard.summary.complianceDescription'),
      progressPercent: clampPercent(
        summary.overallCompliance.score,
        summary.overallCompliance.max,
      ),
    },
    {
      id: 'avg-response',
      title: t('scorecard.summary.averageResponse'),
      metric: formatHours(summary.averageResponseHours.value),
      description: t('scorecard.summary.targetHours', {
        target: summary.averageResponseHours.target,
      }),
      progressPercent: hoursTowardTargetPercent(
        summary.averageResponseHours.value,
        summary.averageResponseHours.target,
      ),
    },
    {
      id: 'avg-fix',
      title: t('scorecard.summary.averageFix'),
      metric: formatHours(summary.averageFixHours.value),
      description: t('scorecard.summary.targetHours', {
        target: summary.averageFixHours.target,
      }),
      progressPercent: hoursTowardTargetPercent(
        summary.averageFixHours.value,
        summary.averageFixHours.target,
      ),
    },
    {
      id: 'availability',
      title: t('scorecard.summary.overallAvailability'),
      metric: `${summary.overallAvailabilityPercent.value}%`,
      description: t('scorecard.summary.targetPercent', {
        target: summary.overallAvailabilityPercent.target,
      }),
      progressPercent: clampPercent(
        summary.overallAvailabilityPercent.value,
        100,
      ),
    },
  ];
}

export function mapOperatorCards(
  stats: OperatorScorecardStatsDto,
  locale: string,
  t: TFunction<'dashboard'>,
): OperatorCardViewModel[] {
  return stats.operators.map((operator) => ({
    id: operator.id,
    name: operator.name,
    summary: operator.summary,
    responseLabel: t('scorecard.operator.response'),
    responseValue: `${operator.responseHours}h`,
    uptimeLabel: t('scorecard.operator.uptime'),
    uptimeValue: `${operator.uptimePercent}%`,
    sitesLabel: t('scorecard.operator.sitesRun'),
    sitesValue: String(operator.sitesRun),
    penaltiesLabel: t('scorecard.operator.penalties'),
    penaltiesValue: formatPenaltiesEuros(operator.penaltiesEuros, locale),
    performanceLabel: t('scorecard.operator.performance'),
    performancePercent: operator.performancePercent,
    performanceBand: operator.performanceBand,
    performanceBandLabel: t(
      `scorecard.operator.band.${operator.performanceBand}`,
    ),
    performanceStroke: PERFORMANCE_STROKE[operator.performanceBand],
  }));
}

export function mapFailingSiteRows(
  stats: OperatorScorecardStatsDto,
  t: TFunction<'dashboard'>,
): FailingSiteRowViewModel[] {
  return stats.failingSites.map((row) => ({
    id: row.id,
    site: row.site,
    region: row.region,
    performance: `${row.performanceCurrent}/${row.performanceMax}`,
    operator: row.operator,
    statusTone: STATUS_TONE[row.status],
    statusLabel: t(`scorecard.failingSites.status.${row.status}`),
  }));
}

export function paginateRows<T>(
  rows: T[],
  page: number,
  pageSize: number,
): T[] {
  const start = (page - 1) * pageSize;
  return rows.slice(start, start + pageSize);
}
